import { Appointment } from "components/rep/accounts/cardNewDesign/types";
import moment from "moment";
import toast from "react-hot-toast";
import Holidays from "date-holidays";
import { PLANS_DATA } from "components/payments/constants";

const DATE_FORMAT: string = "M/D/YY";
const APPOINTMENT_DAY_FORMAT: string = "MMM Do, YYYY";
const TIME_FORMAT: string = "h:mma";
const WEEK_DAY: string = "dddd";
const REFERRAL_URL = {
  development: "http://localhost:3000/client?referral=",
  staging: "https://stage.myrepapp.com/client?referral=",
  production: "https://myrepapp.com/client?referral=",
};
interface iNotificationType {
  label?: string;
  emailSubtext?: string;
  smsSubtext?: string;
  emailProp?: string;
  smsProp?: string;
  allowedRoles?: string[];
}
interface iReferralUrls {
  development: string;
  staging: string;
  production: string;
}
const ROLE_NAMES = {
  BASIC: "Basic",
  PLUS: "Plus",
  PREMIER: "Premier",
  ADMIN: "Admin",
  CLIENT: "Client",
  DELEGATED_USER: "Delegated User",
  OFFICE_KIOSK: "Office Kiosk",
};

const NOTIFICATION_TYPES: iNotificationType[] = [
  {
    label: "Appointment Reminder",
    emailSubtext: "Receive an email a day before the appointment.",
    smsSubtext:
      "Receive a text message on your phone a day before the appointment.",
    emailProp: "aptReminderEmail",
    smsProp: "aptReminderSms",
    allowedRoles: [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
  {
    label: "Appointment Cancellation",
    emailSubtext:
      "Receive an email if your appointment is cancelled by the office.",
    smsSubtext:
      "Receive a text message on your phone if your appointment is cancelled by the office.",
    emailProp: "aptCancellationEmail",
    smsProp: "aptCancellationSms",
    allowedRoles: [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
  {
    label: "Appointment Opening",
    emailSubtext:
      "Receive an email when new appointment is available for the office you follow.",
    smsSubtext:
      "Receive a text message on your phone when new appointment is available for the office you follow.",
    emailProp: "aptOpeningEmail",
    smsProp: "aptOpeningSms",
    allowedRoles: [ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
  {
    label: "Appointment Transfer",
    emailSubtext:
      "Receive an email on when you receive a transfer of an appointment from another premier user.",
    smsSubtext:
      "Receive a text message on your phone when you receive a transfer of an appointment from another premier user.",
    emailProp: "aptTransferEmail",
    smsProp: "aptTransferSms",
    allowedRoles: [ROLE_NAMES.PREMIER],
  },
  {
    label: "Direct Messages from Offices",
    smsSubtext:
      " Receive a text message on your phone when an office sends you a direct message via email.",
    smsProp: "ofcMessageSms",
    allowedRoles: [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
];
const getDisplayDate = (date: string) => {
  return moment(date).format("M/D/YY");
};

const makeToast = (message: string, type: string, time: number = 3000) => {
  const t =
    type === "success" ? toast.success : type === "error" ? toast.error : toast;
  t(message, {
    duration: time,
    position: "top-center",
  });
};
const sortAppointments = (appointments: Appointment[]) => {
  return [...appointments].sort((a, b) => {
    const dateA = moment(a.date, "YYYY-MM-DD");
    const dateB = moment(b.date, "YYYY-MM-DD");

    return dateA.diff(dateB);
  });
};
const formatTime = (appointmentTimeInSeconds: number) => {
  const hours = Math.floor(appointmentTimeInSeconds / 3600) % 12;
  const minutes = Math.floor((appointmentTimeInSeconds % 3600) / 60);
  const isAM = Math.floor(appointmentTimeInSeconds / 3600) < 12;

  const formattedHours = hours === 0 ? 12 : hours;

  const formattedMinutes = String(minutes).padStart(2, "0");

  const ampm = isAM ? "AM" : "PM";

  return `${formattedHours}:${formattedMinutes} ${ampm}`;
};
const getRepeatingDates = (
  startDateStr: string,
  endDateStr: string,
  repeatingDays: number[],
  repeatYearly: boolean = false
): string[] => {
  const dates = [];
  let startDate, endDate;
  const today = moment().startOf("day");
  if (repeatYearly) {
    startDate = today;
    endDate = moment().endOf("year");
  } else {
    startDate = moment(startDateStr, "MM/DD/YYYY");
    endDate = moment(endDateStr, "MM/DD/YYYY");
    if (startDate.isBefore(today)) {
      startDate = today;
    }
  }

  while (startDate.isSameOrBefore(endDate)) {
    if (repeatingDays.includes(startDate.day())) {
      dates.push(startDate.format("YYYY-MM-DD"));
    }
    startDate.add(1, "day");
  }

  return dates;
};
const convertToDateTimeSlots = (
  templateDates: string[],
  templateTimes: number[]
): string[] => {
  return templateDates.flatMap((date) =>
    templateTimes.map((timeInSeconds) => {
      const hours = Math.floor(timeInSeconds / 3600);
      const minutes = (timeInSeconds % 3600) / 60;
      const dateObj = new Date(
        `${date}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:00.000Z`
      );
      return dateObj.toISOString();
    })
  );
};

const processTimestamps = (timestamps: string[]) => {
  const dates = timestamps.map((date) => new Date(date));

  const startDate = moment(
    new Date(Math.min(...dates.map((d) => d.getTime())))
  ).format("MM/DD/YYYY");
  const endDate = moment(
    new Date(Math.max(...dates.map((d) => d.getTime())))
  ).format("MM/DD/YYYY");
  const timeSet = new Set(dates.map((d) => moment.utc(d).format("h:mmA")));
  const times = Array.from(timeSet)
    .sort((a, b) => {
      const dateA = moment(`2000-01-01 ${a}`, "YYYY-MM-DD h:mmA");
      const dateB = moment(`2000-01-01 ${b}`, "YYYY-MM-DD h:mmA");
      return dateA.valueOf() - dateB.valueOf();
    })
    .map((time: string) => {
      const momentTime = moment(time, "h:mmA");
      return momentTime.hours() * 60 + momentTime.minutes();
    });

  return {
    startDate,
    endDate,
    times,
  };
};
const isArrayEqual = (arr1: number[], arr2: number[]) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((value, index) => value === arr2[index]);
};
const getAddedAndRemovedDates = (
  newDateTimeSlots: string[],
  oldDateTimeSlots: string[]
) => {
  const newSet = new Set(newDateTimeSlots);
  const oldSet = new Set(oldDateTimeSlots);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const addedDates = newDateTimeSlots.filter((date) => !oldSet.has(date));
  const removedDates = oldDateTimeSlots.filter(
    (date) => !newSet.has(date) && new Date(date) >= today
  );

  return { addedDates, removedDates };
};
const HOME_SECTIONS = {
  benefits: "benefitsRef",
  howItWorks: "howItWorksRef",
  testimonial: "testimonialsRef",
  faq: "faqRef",
};

const getHolidays = () => {
  const hd = new Holidays("US");
  const holidays = [
    ...hd.getHolidays(moment().subtract(1, "year").format("YYYY")),
    ...hd.getHolidays(moment().format("YYYY")),
    ...hd.getHolidays(moment().add(1, "year").format("YYYY")),
  ].map((value) => {
    const data = {
      ...value,
      date: value.date.substring(0, 10),
      title: value.name,
      start: value.date.substring(0, 10),
      type: value.type,
    };

    return data;
  });
  return holidays;
};

const getUserReferralLink = (userId: number) => {
  return `${
    REFERRAL_URL[process.env.NODE_ENV as keyof iReferralUrls]
  }${userId}`;
};
const annotationColorParser = (obj: {
  bg?: string;
  text?: string;
  border?: string;
}): string => {
  return `bg=${obj?.bg || "#fffff"}&&text=${obj?.text || "#2d4484"}&&border=${
    obj?.border || "#bcbcbc"
  }`;
};

const annotationColorDeparser = (
  str: string
): { bg: string; text: string; border: string } => {
  const parts = str.split("&&");
  const result: { [key: string]: string } = {};
  parts.forEach((part) => {
    const [key, value] = part.split("=");
    result[key.trim()] = value ? value.trim() : "";
  });
  return {
    bg: result.bg || "",
    text: result.text || "",
    border: result.border || "",
  };
};
const getDynamicPlans = (config: any[]) => {
  const configMap = Object.fromEntries(config.map((c) => [c.key, c.value]));

  return PLANS_DATA.map((plan) => {
    if (plan.planId === 4) {
      // Plus plan
      return {
        ...plan,
        monthlyPrice: parseFloat(
          configMap["price_plus_monthly"] ?? plan.monthlyPrice
        ),
        yearlyPrice: parseFloat(
          configMap["price_plus_annual"] ?? plan.yearlyPrice
        ),
      };
    }

    if (plan.planId === 5) {
      // Premier plan
      return {
        ...plan,
        monthlyPrice: parseFloat(
          configMap["price_premier_monthly"] ?? plan.monthlyPrice
        ),
        yearlyPrice: parseFloat(
          configMap["price_premier_annual"] ?? plan.yearlyPrice
        ),
      };
    }

    return plan;
  });
};
export {
  DATE_FORMAT,
  WEEK_DAY,
  getDisplayDate,
  TIME_FORMAT,
  makeToast,
  APPOINTMENT_DAY_FORMAT,
  HOME_SECTIONS,
  sortAppointments,
  formatTime,
  ROLE_NAMES,
  NOTIFICATION_TYPES,
  iNotificationType,
  getRepeatingDates,
  convertToDateTimeSlots,
  processTimestamps,
  isArrayEqual,
  getAddedAndRemovedDates,
  getHolidays,
  REFERRAL_URL,
  iReferralUrls,
  getUserReferralLink,
  annotationColorParser,
  annotationColorDeparser,
  getDynamicPlans,
};
